import React, {
  useRef,
  useEffect,
  useContext,
  useState,
  Fragment,
} from 'react';
import { connect } from 'react-redux';
import { compose, withApollo } from 'react-apollo';
import { isNil, isObject } from 'lodash';
import cn from 'classnames';
import { getSubGroupStudentsLabel } from 'legacy-src/utility/utils';
import HorizontalTabs from 'chameleon/ui-stack/ui/base/tabs/horizontal-tabs';
import Header from 'chameleon/ui-stack/ui/page/header';
import ClassDropdown from 'legacy/src/components/assignment/Controls/ClassDropdown/ClassDropdown';
import { BreadcrumbContext } from 'chameleon/ui-stack/ui/basic/breadcrumbs';
import useFetchTeacherAssignments from 'chameleon/ui-stack/hooks/use-assignments-fetch/teacher';
import { LABELS } from 'legacy/src/components/assessment/Results/shared/labels';
import Button from 'chameleon/ui-stack/ui/base/button';
import { AllAssignments } from 'legacy/src/utility/utils';
import { STATUS } from 'chameleon-src/constants/assessmentStatus';
import Actions from 'chameleon/ui-stack/ui/assessments/actions';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import { renderPerformanceBar, StartDueLabels } from './utils/utils';

const ClassResultsContainer = (props) => {
  const {
    assignmentResultsLoading,
    resourcesTabLabel,
    assignment,
    students,
    isPPC, // N.B: other assessment types taken from the assignment.assessment
    params,
    router,
    results,
    location: { pathname, search },
    userData: { isStudent },
    showFrqResponses,
    hasFrqQuestions,
    assignmentControlsDropdownFlag,
    masteryReportFlag,
    resultsAssignmentActionsFlag,
    immediateFeedbackFlag,
    classes,
    studentsBySection,
    subjectData,
    me,
    activeEducationPeriodFromFiltersControl,
    chosenEducationPeriodCode,
  } = props;

  const [learnositySignedRequest, setLearnositySignedRequest] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const questionsTableSort = useRef(null);
  const [getTeacherAssignments] = useFetchTeacherAssignments();
  const [startDueTimes, setStartDueTimes] = useState(null);
  const sectionIds = classes ? classes.items.map((clazz) => clazz.id) : [];
  const {
    location: { state: currentRouterState },
  } = router;

  const { viewResultOverviewFromReportPage } = currentRouterState || {};

  const educationalPeriod = viewResultOverviewFromReportPage
    ? activeEducationPeriodFromFiltersControl
    : chosenEducationPeriodCode;

  const {
    students: assignmentStudents,
    title,
    isIndividualAssignment,
    sectionName,
    status,
    assessment,
  } = assignment;
  const { isSpp, isLearningCheckpoint, isPerformanceTask } = assessment || {};

  const assignmentActions =
    (assessments || [])
      ?.filter(({ id: assessmentId }) => assessmentId === assessment?.id)[0]
      ?.assignments?.filter(
        ({ id: assignmentId }) => assignmentId === assignment?.id,
      )[0]?.actions || [];

  /**
   * We filter these actions down to make sure we don't make any more changes
   * than we have to. i.e. by not filtering down the actions we would be adding
   * actions like delete and view_results (this is especially wrong since we're
   * already on the Results page)
   */
  const markCompleteActions = assignmentActions.filter((assignmentAction) =>
    ['reopen', 'mark_complete'].includes(assignmentAction),
  );

  const newActions = [
    ...markCompleteActions,
    'share_link',
    'update_settings',
    'upload_answer_sheets',
    'download_answer_sheets',
    'show_available_results_for_students',
    'show_go_to_assignment_overview',
  ];

  const assessmentTypesWithoutCSP =
    isSpp || isLearningCheckpoint || isPerformanceTask;

  const showCSPTab =
    masteryReportFlag && educationalPeriod >= 24 && !assessmentTypesWithoutCSP;

  const allTabs = [
    isLearningCheckpoint && {
      key: 'summary',
      isActive: pathname.includes('summary'),
      name: 'Summary',
      onClick: () =>
        router.push(
          `/${params.subject}/assessments/results/${params.assignmentId}/summary-report`,
        ),
    },
    showCSPTab && {
      key: 'performance',
      isActive: pathname.includes('performance'),
      name: 'Content & Skills Performance',
      onClick: () =>
        router.push(
          `/${params.subject}/assessments/results/${params.assignmentId}/csperformance`,
        ),
    },
    isPPC &&
      !isNil(resourcesTabLabel) && {
        key: 'resources',
        isActive: pathname.includes('resources'),
        name: resourcesTabLabel,
        onClick: () =>
          router.push(
            `/${params.subject}/assessments/results/${params.assignmentId}/resources`,
          ),
      },
    {
      key: 'questions',
      isActive: pathname.includes('questions'),
      name: 'Questions',
      onClick: () =>
        router.push(
          `/${params.subject}/assessments/results/${params.assignmentId}/questions`,
        ),
    },
    {
      key: 'students',
      isActive: pathname.includes('students'),
      name: 'Students',
      onClick: () =>
        router.push(
          `/${params.subject}/assessments/results/${params.assignmentId}/students`,
        ),
    },
  ];
  const availableTabs = allTabs.filter((tab) => isObject(tab));

  const hasResourcesTab = availableTabs.some(
    (tab) => isObject(tab) && tab.key === 'resources',
  );
  const { dispatch, ACTIONS } = useContext(BreadcrumbContext);

  useEffect(() => {
    if (!isStudent) {
      const { subject, assignmentId, questionId, partId } = params;
      let routePath = `/${subject}/assessments/results/${assignmentId}/questions`;
      if (questionId) {
        routePath = `/${subject}/assessments/results/${assignmentId}/questions/${questionId}`;
      }
      if (partId) {
        routePath = `/${subject}/assessments/results/${assignmentId}/questions/${questionId}/parts/${partId}`;
      }
      if (hasResourcesTab) {
        routePath = `/${subject}/assessments/results/${assignmentId}/resources`;
      }

      /******************************************************************************
       * currentRouterState is preserved here because
       * viewResultOverviewFromReportPage from currentRouterState is required for deciding
       * if active or filterControl-selected education period should be used as the education period
       * When loadAssignments function is called in ClassResultsContainer.
       * *****************************************************************************/
      router.replace({
        // Note: We're using replace instead of push due to the issue mentioned in https://academicmerit.atlassian.net/browse/FY-34885
        pathname: routePath,
        state: { ...currentRouterState },
      });
      dispatch({
        type: ACTIONS.updateResultsOverviewRoute,
        payload: routePath,
      });
    } else {
      // something is wrong here,
      // if the user is a student, we should not be here
      // trigger a manual refresh (because of the impersonation)
      window.location.reload();
    }
  }, [assignmentResultsLoading]);

  const loadAssignments = () => {
    const fetchProps = {
      subject: params.subject,
      status: STATUS.ALL_ASSIGNED,
      resourceType: {},
      learnositySignedRequest,
      setData: setAssessments,
      setOptions: () => {},
      setLearnositySignedRequest,
      setLoading,
      sections: sectionIds,
      setHasPaperscore: () => {},
      educationalPeriod: viewResultOverviewFromReportPage
        ? activeEducationPeriodFromFiltersControl
        : chosenEducationPeriodCode,
    };
    getTeacherAssignments({ ...fetchProps });
  };

  useEffect(() => {
    loadAssignments();
  }, []);

  const availableStudents = assignmentStudents || students;
  const subGroupStudentsLabel =
    isIndividualAssignment && getSubGroupStudentsLabel(assignmentStudents);
  const viewingQuestionDetail = !isNil(params.questionId);

  const hasAsessments = assessments && assessments.length;
  const allAssignments =
    hasAsessments && new AllAssignments(assessments, assessment.id);
  const assignmentsWithResults =
    hasAsessments && allAssignments.getAssignmentsWithResults();

  let firstStudentId;
  let isStudentResponsesAvailable;
  if (showFrqResponses && hasFrqQuestions && !params.ppcPartId) {
    const { studentSessions } = assignment;
    const studentScoredSessions = (studentSessions || []).filter(
      ({ scoringCompletedAt, submittedAt }) =>
        !!scoringCompletedAt && (!isSpp || (isSpp && submittedAt)),
    );
    isStudentResponsesAvailable = (studentScoredSessions || []).length > 0;
    firstStudentId =
      (isStudentResponsesAvailable && studentScoredSessions[0].userId) || null;
  }
  const onButtonClick = () => {
    const url_question = viewingQuestionDetail
      ? `/questions/${params.questionId}`
      : '';
    const { subject, assignmentId } = params || {};
    let url = `/${subject}/assessments/results/${assignmentId}/students/${firstStudentId}/responses${url_question}`;
    if (isSpp) {
      const returnPath = `${pathname}${search || ''}`;
      url = {
        pathname: `/${subject}/assessments/spp/review/${assignmentId}/students/${firstStudentId}`,
        state: { returnPath },
      };
    }
    router.push(url);
  };

  const onClassDropdownChange = (option) => {
    const redirectUrl = `/${params.subject}/assessments/results/${option.value}/questions`;
    router.push(redirectUrl);
    dispatch({
      type: ACTIONS.updateResultsOverviewRoute,
      payload: redirectUrl,
    });
  };

  const selectedAssignment = allAssignments && {
    ...allAssignments.assignments.find((a) => a.id === assignment.id),
  };

  const selectedAssignmentWithUpdatedActions = allAssignments && {
    ...selectedAssignment,
    actions: newActions,
  };

  return (
    <React.Fragment>
      <Header
        subtitle={
          viewingQuestionDetail ? 'QUESTION DETAILS' : 'RESULTS OVERVIEW'
        }
        title={title}
      />
      <div className="flex mobile:flex-col justify-center pb-10 text-gray-700 gap-14 items-center">
        <StartDueLabels
          assignment={
            startDueTimes
              ? {
                  ...assignment,
                  starts_at: startDueTimes.startsAt,
                  due_at: startDueTimes.dueAt,
                }
              : assignment
          }
        />
        {immediateFeedbackFlag && assignment.hasImmediateFeedback && (
          <span className="flex items-center">
            <Icon name="immediate_feedback" />{' '}
            <label className="pl-4">Immediate Feedback ON</label>
          </span>
        )}
        {isStudentResponsesAvailable && (
          <Button
            isPrimary
            onClick={onButtonClick}
            data-test-id="view-all-student-responses"
          >
            {LABELS.ALL_STUDENTS_RESPONSES}
          </Button>
        )}
        {resultsAssignmentActionsFlag && me.isTeacher && (
          <Fragment>
            {!isSpp &&
              hasFrqQuestions &&
              assignment.progress.scoring + assignment.progress.submitted >
                0 && (
                <a
                  className="text-base font-normal d-flex flex-row justify-center items-center rounded-full p-2 pr-4 bg-blue-700 hover:bg-blue-800 text-white"
                  href={`${FYM_FRONTEND_URL}/${params.subject}/assignments/progress/${params.assignmentId}`}
                >
                  <div className="d-flex flex-row items-center justify-center w-10 h-10 bg-blue-900 mr-2 rounded-full">
                    {assignment.progress.scoring +
                      assignment.progress.submitted}
                  </div>
                  To Score
                </a>
              )}
            {allAssignments ? (
              <Actions
                assessment={allAssignments.assessment}
                assignment={selectedAssignmentWithUpdatedActions}
                hasSubmitUnsubmit
                isButton
                hasOneInProgressSubmission={assignment.studentSessions.some(
                  (a) => a.submittedAt !== null,
                )}
                userIdsWithProgress={assignment.studentSessions
                  .filter((s) => s.submittedAt !== null)
                  .map((s) => s.userId)}
                fetchAssessmentData={loadAssignments}
                subjectInitId={subjectData.subjects[0]}
                hasStudents={(assignment.students || []).length > 0}
                startsAt={selectedAssignmentWithUpdatedActions.starts_at}
                dueAt={selectedAssignmentWithUpdatedActions.due_at}
                type={allAssignments?.assessment?.type}
                isSPP={allAssignments?.assessment?.is_spp}
                setStartDueTimes={setStartDueTimes}
                activeEducationPeriodFromFiltersControl={
                  viewResultOverviewFromReportPage
                    ? Number(activeEducationPeriodFromFiltersControl)
                    : chosenEducationPeriodCode
                }
              />
            ) : (
              <React.Fragment />
            )}
          </Fragment>
        )}
      </div>
      <div className="flex justify-center mb-10">
        {(assignmentControlsDropdownFlag && (
          <ClassDropdown
            openAssignments={assignmentsWithResults}
            sectionName={sectionName}
            onChange={onClassDropdownChange}
            assignmentId={params.assignmentId}
            status={status}
            isLoading={isLoading}
            subGroupStudentsLabel={subGroupStudentsLabel}
            studentsBySection={studentsBySection}
            blackListedStatus={[]}
          />
        )) ||
          null}
      </div>
      {renderPerformanceBar({
        params,
        results,
        assignment,
        students: availableStudents,
        hideTooltip: true,
      })}
      <HorizontalTabs
        tabs={availableTabs}
        direction="horizontal"
        accessibility={{
          ariaLabel: ' Class Results Tabs',
        }}
        classNames={{
          tab: `flex flex-1 justify-center px-5 py-8 tab`,
        }}
        tabsClassName={cn('justify-around my-12', {
          'pb-8': isSpp,
        })}
      />
      {React.cloneElement(props.children, { ...props, questionsTableSort })}
    </React.Fragment>
  );
};

const mapStateToProps = ({
  featureFlag: {
    show_frq_responses: showFrqResponses,
    assignment_controls_dropdown: assignmentControlsDropdownFlag,
    mastery_report: masteryReportFlag,
    results_assignment_actions: resultsAssignmentActionsFlag,
    reorder_questions: canReorderQuestionsFlag,
    immediate_feedback: immediateFeedbackFlag,
  },
  filterControls: { activeEducationPeriod },
  userData: { chosenEducationPeriodCode },
  subject,
}) => ({
  subjectData: subject.data,
  showFrqResponses,
  assignmentControlsDropdownFlag,
  masteryReportFlag,
  resultsAssignmentActionsFlag,
  immediateFeedbackFlag,
  canReorderQuestionsFlag,
  activeEducationPeriodFromFiltersControl: activeEducationPeriod,
  chosenEducationPeriodCode,
});

export default compose(
  withApollo,
  connect(mapStateToProps, {}),
)(ClassResultsContainer);
